/**
 * One-shot Home Screen PWA refresh onto a new service-worker cache version.
 * Call once from App bootstrap with the platform's current CACHE_VERSION string
 * (must match public/sw.js). Clears old caches and reloads when the build changes.
 *
 * Never runs under Vite DEV: registering /sw.js on :3005 (etc.) fights main.tsx
 * unregister + Platzel SW self-destruct (`client.navigate`), which blanks iOS
 * Home Screen standalone while the Safari tab still looks fine.
 */
export function installPwaForceUpdate(expectCacheVersion: string): void {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  if (!expectCacheVersion) return;
  // Vite DEV injects /@vite/client — never force-register SW there (Platzel SW on
  // :3005 self-destructs with client.navigate and blanks iOS Home Screen PWAs).
  if (document.querySelector('script[src*="@vite/client"]')) return;
  // Localtest / LAN preview gate (:3105, :3115, :3116) — never register; a sticky
  // worker there cache-blocks /api ("Failed to fetch") and hides rebuilt UI.
  const port = window.location.port || '';
  if (port === '3105' || port === '3115' || port === '3116') return;

  const key = 'platzona-sw-build';
  const buildMetaKey = 'platzona-build-id';
  const reloadGuardKey = 'platzona-sw-reload-guard';

  // Break an in-progress reload blitz (e.g. after a bad force-update build).
  try {
    const guarded = sessionStorage.getItem(reloadGuardKey);
    if (guarded) {
      localStorage.setItem(key, guarded);
      sessionStorage.removeItem(reloadGuardKey);
      return;
    }
    // Hard stop: at most one forced reload per tab session for this helper.
    // Without this, mismatched App↔sw.js CACHE_VERSION or foreign caches could
    // blink Freigabe (platzel.de) forever.
    if (sessionStorage.getItem(`${reloadGuardKey}:done`) === '1') {
      return;
    }
  } catch {
    /* ignore */
  }

  const metaBuild =
    document.querySelector('meta[name="platzona-build-id"]')?.getAttribute('content') || '';

  fetch(`/sw.js?_=${Date.now()}`, { cache: 'no-store' })
    .then((r) => r.text())
    .then(async (text) => {
      // Prefer the live SW version so a stale App bundle still upgrades.
      const live =
        (text.match(/CACHE_VERSION\s*=\s*['"]([^'"]+)['"]/) || [])[1] || expectCacheVersion;
      if (!text.includes(live) && !text.includes(expectCacheVersion)) return;
      const target = text.includes(expectCacheVersion) ? expectCacheVersion : live;

      let prev: string | null = null;
      let prevBuild: string | null = null;
      try {
        prev = localStorage.getItem(key);
        prevBuild = localStorage.getItem(buildMetaKey);
      } catch {
        /* ignore */
      }

      const cacheKeys = await caches.keys().catch(() => [] as string[]);
      // Only treat THIS platform's SW caches as stale. Unrelated caches (other
      // Platzona apps on a shared origin, Workbox leftovers) must not force a
      // reload loop via `k !== target`.
      const stem = target.split('-').slice(0, 2).join('-'); // e.g. platzona-messenger
      const ownCaches = stem
        ? cacheKeys.filter((k) => k === target || k.startsWith(`${stem}-`))
        : cacheKeys.filter((k) => k === target);
      const hasStaleCaches = ownCaches.some((k) => k !== target);
      // First boot after this helper, or a new dist build-id, must refresh.
      const buildChanged = Boolean(metaBuild) && prevBuild !== metaBuild;
      const versionChanged = prev !== target;

      if (!versionChanged && !hasStaleCaches && !buildChanged) {
        const reg = await navigator.serviceWorker.getRegistration().catch(() => undefined);
        if (reg) void reg.update().catch(() => undefined);
        return;
      }

      await Promise.all(ownCaches.map((k) => caches.delete(k)));

      const reg = await navigator.serviceWorker.getRegistration().catch(() => undefined);
      if (!reg) {
        await navigator.serviceWorker.register(`/sw.js?v=${encodeURIComponent(target)}`, {
          updateViaCache: 'none',
        });
      } else {
        await reg.update().catch(() => undefined);
        if (reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        if (reg.installing) reg.installing.postMessage({ type: 'SKIP_WAITING' });
      }

      const finish = () => {
        try {
          localStorage.setItem(key, target);
          if (metaBuild) localStorage.setItem(buildMetaKey, metaBuild);
        } catch {
          /* ignore */
        }
        try {
          localStorage.removeItem('pv-sw-build');
        } catch {
          /* ignore */
        }
        try {
          sessionStorage.setItem(reloadGuardKey, target);
          sessionStorage.setItem(`${reloadGuardKey}:done`, '1');
        } catch {
          /* ignore */
        }
        location.reload();
      };

      if (navigator.serviceWorker.controller) {
        let done = false;
        const once = () => {
          if (done) return;
          done = true;
          finish();
        };
        navigator.serviceWorker.addEventListener('controllerchange', once, { once: true });
        setTimeout(once, 900);
      } else {
        finish();
      }
    })
    .catch(() => {
      /* ignore offline / local-only failures */
    });
}
